import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { radii } from '../../theme';

const toArray = (sx?: SxProps<Theme>) => (sx === undefined ? [] : Array.isArray(sx) ? sx : [sx]);

interface SectionCardProps {
  children?: React.ReactNode;
  sx?: SxProps<Theme>;
  id?: string;
}

/** Outermost dashboard surface: one per chart/table section. */
export const SectionCard: React.FC<SectionCardProps> = ({ children, sx, id }) => (
  <Box
    component="section"
    id={id}
    sx={[
      {
        position: 'relative',
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        borderRadius: `${radii.panel}px`,
        p: { xs: 2, md: 3 },
        minWidth: 0,
      },
      ...toArray(sx),
    ]}
  >
    {children}
  </Box>
);

/** A block that sits inside a SectionCard: tinted, no border, smaller radius. */
export const NestedPanel: React.FC<SectionCardProps> = ({ children, sx, id }) => (
  <Box
    id={id}
    sx={[
      (theme) => ({
        bgcolor: theme.ink.surfaceAlt,
        borderRadius: `${radii.nested}px`,
        p: { xs: 2, md: 2.5 },
        minWidth: 0,
      }),
      ...toArray(sx),
    ]}
  >
    {children}
  </Box>
);

interface SectionHeaderProps {
  title: React.ReactNode;
  /** Explanation shown on hover of the info icon next to the title. */
  info?: React.ReactNode;
  /** One or two sentences under the title. */
  description?: React.ReactNode;
  /** Controls aligned to the right of the title (toggles, selects). */
  actions?: React.ReactNode;
  /** Use 'sub' for a heading inside a section (e.g. one chart of several). */
  level?: 'section' | 'sub';
  sx?: SxProps<Theme>;
}

export const SectionHeader: React.FC<SectionHeaderProps> = ({
  title,
  info,
  description,
  actions,
  level = 'section',
  sx,
}) => (
  <Box sx={[{ mb: level === 'section' ? 2.5 : 1.5 }, ...toArray(sx)]}>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        columnGap: 2,
        rowGap: 1.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
        <Typography
          variant={level === 'section' ? 'h5' : 'h6'}
          component={level === 'section' ? 'h2' : 'h3'}
          sx={{ color: 'text.primary' }}
        >
          {title}
        </Typography>
        {info && (
          <Tooltip title={info} placement="top">
            <InfoOutlinedIcon
              tabIndex={0}
              aria-label="About this section"
              sx={{
                fontSize: 18,
                color: 'text.secondary',
                opacity: 0.7,
                cursor: 'help',
                '&:hover, &:focus-visible': { opacity: 1, outline: 'none', color: 'primary.main' },
              }}
            />
          </Tooltip>
        )}
      </Box>
      {actions && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>{actions}</Box>
      )}
    </Box>
    {description && (
      <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.75, maxWidth: '80ch' }}>
        {description}
      </Typography>
    )}
  </Box>
);

/** Centered spinner + message used while a section's first load is in flight. */
export const SectionLoading: React.FC<{ label?: string; height?: number }> = ({ label, height = 320 }) => (
  <Box
    sx={{
      height,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 1.5,
      color: 'text.secondary',
    }}
  >
    <Box
      component="span"
      sx={{
        width: 28,
        height: 28,
        borderRadius: '50%',
        border: 2.5,
        borderColor: 'divider',
        borderTopColor: 'primary.main',
        animation: 'ink-spin 0.9s linear infinite',
        '@keyframes ink-spin': { to: { transform: 'rotate(360deg)' } },
        '@media (prefers-reduced-motion: reduce)': { animationDuration: '2.4s' },
      }}
    />
    {label && <Typography variant="body2">{label}</Typography>}
  </Box>
);

/** Small legend swatch + label, shared by the custom chart legends. */
export const LegendItem: React.FC<{ color: string; label: React.ReactNode; muted?: boolean }> = ({
  color,
  label,
  muted,
}) => (
  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, opacity: muted ? 0.4 : 1 }}>
    <Box component="span" sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: color, flexShrink: 0 }} />
    <Typography variant="body2" component="span" sx={{ fontSize: '0.8125rem', color: 'text.primary' }}>
      {label}
    </Typography>
  </Box>
);

/**
 * Sentiment score as a tinted badge: green above +0.1, red below -0.1,
 * neutral grey in between (same thresholds the tables used before).
 */
export const SentimentBadge: React.FC<{ value: number; digits?: number }> = ({ value, digits = 2 }) => (
  <Box
    component="span"
    sx={(theme) => {
      const tone =
        value > 0.1 ? theme.ink.positive : value < -0.1 ? theme.ink.negative : theme.palette.text.secondary;
      return {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 52,
        px: 1,
        py: 0.25,
        borderRadius: '6px',
        fontSize: '0.8125rem',
        fontWeight: 600,
        fontVariantNumeric: 'tabular-nums',
        color: tone,
        bgcolor: alpha(tone, theme.palette.mode === 'dark' ? 0.18 : 0.1),
      };
    }}
  >
    {value > 0 ? '+' : ''}
    {value.toFixed(digits)}
  </Box>
);
